import { useEffect, useLayoutEffect, useRef, useId } from 'react';
import ActivityStatus from './ActivityStatus.jsx';

/** A <dialog> that opens and closes with a React prop instead of an imperative
 * handle. The owner decides whether Escape/backdrop dismissal is allowed.
 * Multi-stage field reviews can focus their heading and restore a stable trigger. */
export default function Modal({ open, onClose, children, width, focusKey, returnFocusRef, className }) {
  const ref = useRef(null);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open) {
      const title = d.querySelector('h2');
      if (title) { title.id ||= titleId; d.setAttribute('aria-labelledby', title.id); }
      if (!d.open) d.showModal();
      if (focusKey !== undefined && title) {
        title.tabIndex = -1;
        title.focus({ preventScroll: true });
        d.scrollTop = 0;
        const body = d.querySelector('.dbody');
        if (body) body.scrollTop = 0;
      }
    }
    if (!open && d.open) d.close();
  }, [open, focusKey, titleId]);
  useLayoutEffect(() => {
    const d = ref.current;
    return () => {
      if (!returnFocusRef) return;
      if (d?.open) d.close();
      const trigger = returnFocusRef.current;
      if (trigger?.isConnected && !trigger.disabled) trigger.focus({ preventScroll: true });
    };
  }, [returnFocusRef]);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    const onCancel = e => { e.preventDefault(); onClose(); };
    d.addEventListener('cancel', onCancel);
    return () => d.removeEventListener('cancel', onCancel);
  }, [onClose]);
  return (
    <dialog ref={ref} className={className} style={width ? { maxWidth: `min(94vw, ${typeof width === 'number' ? width + 'px' : width})` } : undefined}
      onKeyDown={e => {
        if (focusKey === undefined || e.key !== 'Tab') return;
        const items = [...ref.current.querySelectorAll('button, input, select, textarea, a[href], summary, [tabindex]')]
          .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && element.getClientRects().length);
        const index = items.indexOf(document.activeElement);
        if (!items.length || index < 0 || e.shiftKey && index === 0 || !e.shiftKey && index === items.length - 1) {
          e.preventDefault();
          (items[e.shiftKey ? items.length - 1 : 0] || ref.current.querySelector('h2'))?.focus();
        }
      }}
      onClick={e => {
        const r = ref.current.getBoundingClientRect();
        if (e.target === ref.current && (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom)) onClose();
      }}>
      {open && children}
      {open && <ActivityStatus embedded />}
    </dialog>
  );
}
